import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { fetchEnquiriesForTenant, updateEnquiry, createEnquiryForTenant, deleteEnquiry } from '../../services/pocketbase';
import { format } from 'date-fns';
import { ENQUIRY_STATUSES, SOURCE_CHANNELS, PRIORITIES, canManageEnquiriesRole, normalizeSourceChannelForDisplay } from '../../config/constants';
import SafeIcon from '../../common/SafeIcon';
import EnquiryReplyModal from './EnquiryReplyModal';
import * as FiIcons from 'react-icons/fi';
import * as XLSX from 'xlsx';

const {
  FiSearch, FiEdit3, FiX, FiCornerUpLeft, FiPlus, FiTrash2, FiDownload, 
  FiAlertTriangle, FiUpload, FiFileText, FiMessageSquare, FiChevronDown, FiChevronUp, 
  FiCheck, FiPhone, FiMail, FiRefreshCcw, FiLoader, FiCheckCircle, FiMoreHorizontal, FiZap,
  FiBookmark, FiCalendar, FiClock, FiInfo, FiSave
} = FiIcons;

// --- HELPERS ---
const normalizeDigits = (v) => String(v || "").replace(/\D/g, "");
const normalizeText = (v) => String(v || "").trim().toLowerCase();

const makeExactDupKey = (row) => {
  const name = normalizeText(row["Full Name"] || row.full_name || "");
  const email = normalizeText(row["Email"] || row.email || "");
  const phone = normalizeDigits(row["Phone"] || row.phone || "");
  return `name:${name}|email:${email}|phone:${phone}`;
};

const hasAllIdentity = (row) => {
  const name = normalizeText(row["Full Name"] || row.full_name || "");
  const email = normalizeText(row["Email"] || row.email || "");
  const phone = normalizeDigits(row["Phone"] || row.phone || "");
  return Boolean(name && email && phone);
};

const normalizeSource = normalizeSourceChannelForDisplay;

const normalizeImportSource = (source) => {
  if (!source) return 'other';
  const s = String(source).trim().toLowerCase();
  if (s.includes('linkedin') || s.includes('linked in')) return 'LinkedIn';
  if (s.includes('airtasker') || s.includes('air tasker')) return 'Airtasker';
  if (s === 'web' || s.includes('website')) return 'Website';
  if (s.includes('refer') || s.includes('referal')) return 'Referral';
  if (s.includes('client hub') || s.includes('client_hub') || s === 'manual') return 'Client Hub (Manual)';
  if (s.includes('facebook')) return 'facebook';
  if (s.includes('instagram')) return 'instagram';
  if (s.includes('google')) return 'google';
  if (s.includes('word_of_mouth') || s.includes('word of mouth')) return 'word_of_mouth';
  return 'other';
};

const normalizeHeaderKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const getImportValue = (row, aliases) => {
  const entries = Object.entries(row || {});
  for (const alias of aliases) {
    const aliasKey = normalizeHeaderKey(alias);
    const match = entries.find(([key]) => normalizeHeaderKey(key) === aliasKey);
    if (match && match[1] !== undefined && match[1] !== null) return match[1];
  }
  return '';
};

const mapImportRow = (row) => {
  const firstName = getImportValue(row, ['First Name', 'Firstname', 'Given Name']);
  const lastName = getImportValue(row, ['Last Name', 'Lastname', 'Surname', 'Family Name']);
  const explicitName = getImportValue(row, ['Full Name', 'Name', 'Contact Name', 'Customer Name', 'Client Name', 'Enquirer Name', 'Enquiry Name']);
  const fullName = String(explicitName || [firstName, lastName].filter(Boolean).join(' ')).trim();
  return {
    full_name: fullName,
    email: String(getImportValue(row, ['Email', 'Email Address', 'E-mail', 'Contact Email']) || '').trim(),
    phone: String(getImportValue(row, ['Phone', 'Phone Number', 'Mobile', 'Mobile Number', 'Telephone', 'Contact Number']) || '').trim(),
    enquiry_type: String(getImportValue(row, ['Type', 'Enquiry Type', 'Inquiry Type', 'Request Type']) || '').trim(),
    service_requested: String(getImportValue(row, ['Service', 'Service Requested', 'Requested Service', 'Service Type']) || '').trim(),
    message: String(getImportValue(row, ['Message', 'Enquiry', 'Inquiry', 'Details', 'Description', 'Comments', 'Notes']) || '').trim(),
    source_channel: normalizeImportSource(getImportValue(row, ['Source', 'Source Channel', 'Channel', 'Lead Source', 'Enquiry Source', 'Inquiry Source']))
  };
};

const normalizeImportEnquiryType = (value) => {
  if (!value) return 'general';
  const s = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (s === 'quote_request' || s === 'quotation' || s === 'quote') return 'quote';
  if (s === 'booking' || s === 'book') return 'booking';
  if (s === 'service' || s === 'service_request' || s === 'service_enquiry') return 'service_request';
  if (s === 'general' || s === 'general_enquiry' || s === 'general_inquiry') return 'general';
  return null;
};

const isValidEmail = (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const formatNoteTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'MMM dd, HH:mm');
};

const getDateInputValue = (value) => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
};

const toPocketBaseDate = (value) => value ? `${getDateInputValue(value) || value} 12:00:00.000Z` : '';

const getFollowUpMeta = (value) => {
  const dateValue = getDateInputValue(value);
  if (!dateValue) return null;
  const due = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = due.getTime() - today.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const days = Math.round(diff / dayMs);
  if (days < 0) return { state: 'overdue', label: `Overdue ${format(due, 'dd MMM yyyy')}`, dateValue, classes: 'bg-red-100 text-red-700 border-red-200' };
  if (days === 0) return { state: 'today', label: `Due today`, dateValue, classes: 'bg-orange-100 text-orange-700 border-orange-200' };
  return { state: 'upcoming', label: `Follow-up ${format(due, 'dd MMM yyyy')}`, dateValue, classes: 'bg-blue-50 text-blue-700 border-blue-200' };
};

const safeParseNotes = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return [];

  let parsed;
  try {
    parsed = JSON.parse(value);
  } catch {
    parsed = value;
  }

  const rawNotes = Array.isArray(parsed) ? parsed : [parsed];
  return rawNotes
    .map((note) => {
      if (typeof note === 'string') {
        return { text: note, created_at: null, created_by: 'Legacy note' };
      }
      if (!note || typeof note !== 'object') return null;
      return {
        ...note,
        text: String(note.text || note.note || '').trim(),
        created_at: note.created_at || note.createdAt || null,
        created_by: note.created_by || note.createdBy || 'Legacy note'
      };
    })
    .filter(note => note?.text)
    .sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
    });
};

const Enquiries = () => {
  const { getEffectiveTenantId, currentUser, currentUserRole } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Productivity Features State
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedEnquiryId, setExpandedEnquiryId] = useState(null);
  const [bulkUpdateData, setBulkUpdateData] = useState({ status: '', priority: '', note: '' });

  // Import Summary State
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  // Permissions
  const role = currentUserRole || "";
  const canDeleteEnquiries = canManageEnquiriesRole(role);

  // Audit State
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [showDuplicatePanel, setShowDuplicatePanel] = useState(false);

  // Filters
  const initialFilters = {
    status: [],
    enquiry_type: [],
    source_channel: [],
    service: '',
    dateFrom: '',
    dateTo: '',
    followUp: 'all'
  };
  const [filters, setFilters] = useState(initialFilters);
  const [savedViews, setSavedViews] = useState([]);
  const [selectedSavedViewId, setSelectedSavedViewId] = useState('');

  // Import State
  const fileInputRef = useRef(null);
  const quickNoteInputRef = useRef(null);
  const [importStats, setImportStats] = useState({ 
    processing: false, 
    processed: 0, 
    total: 0, 
    added: 0, 
    skipped: 0, 
    failed: 0,
    issueRecords: [] 
  });

  // Modals state
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedEnquiryForNotes, setSelectedEnquiryForNotes] = useState(null);
  const [currentNotes, setCurrentNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingPriority, setEditingPriority] = useState(null);

  const [enquiryFormData, setEnquiryFormData] = useState({
    id: null, full_name: '', email: '', phone: '', enquiry_type: 'general', service_requested: '', message: '', source_channel: 'Client Hub (Manual)', status: 'new', priority: 'normal', follow_up_date: '',
  });

  useEffect(() => { fetchEnquiriesData(); }, [getEffectiveTenantId()]);
  useEffect(() => { applyFilters(); }, [enquiries, filters, searchTerm]);
  useEffect(() => {
    const tenantId = getEffectiveTenantId() || 'no-tenant';
    const userId = currentUser?.id || currentUser?.email || 'user';
    const key = `bpas_enquiry_saved_views_v1:${tenantId}:${userId}`;
    try {
      const raw = window.localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      setSavedViews(Array.isArray(parsed) ? parsed : []);
    } catch {
      setSavedViews([]);
    }
    setSelectedSavedViewId('');
  }, [getEffectiveTenantId(), currentUser?.id, currentUser?.email]);

  // Audit Effect
  useEffect(() => {
    const map = new Map();
    (enquiries || []).forEach((rec) => {
      if (!hasAllIdentity(rec)) return;
      const key = makeExactDupKey(rec);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(rec);
    });
    const groups = [];
    for (const [key, items] of map.entries()) {
      if (items.length > 1) groups.push({ key, count: items.length, records: items });
    }
    setDuplicateGroups(groups.sort((a, b) => b.count - a.count));
  }, [enquiries]);

  const statusCounts = useMemo(() => {
    return (filteredEnquiries || []).reduce((acc, curr) => {
      const st = curr.status || "new";
      acc.total = (acc.total || 0) + 1;
      acc[st] = (acc[st] || 0) + 1;
      return acc;
    }, { total: 0, new: 0, in_progress: 0, contacted: 0, converted: 0, lost: 0 });
  }, [filteredEnquiries]);

  const fetchEnquiriesData = async () => {
    try {
      setIsLoading(true);
      const tenantId = getEffectiveTenantId();
      if (tenantId) {
        const data = await fetchEnquiriesForTenant(tenantId);
        setEnquiries(Array.isArray(data) ? data : []);
      }
    } catch (err) { setError(err.message); } finally { setIsLoading(false); }
  };

  const applyFilters = () => {
    let filtered = [...enquiries];
    if (filters.status.length > 0) filtered = filtered.filter(e => filters.status.includes(e.status));
    if (filters.enquiry_type.length > 0) filtered = filtered.filter(e => filters.enquiry_type.includes(e.enquiry_type));
    if (filters.source_channel.length > 0) {
      filtered = filtered.filter(e => filters.source_channel.some(sc => normalizeSource(e.source_channel) === sc));
    }
    
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const sDigits = normalizeDigits(searchTerm);
      filtered = filtered.filter(e => 
        (e.full_name || "").toLowerCase().includes(s) || 
        (e.email || "").toLowerCase().includes(s) ||
        (sDigits && normalizeDigits(e.phone).includes(sDigits))
      );
    }

    if (filters.service) filtered = filtered.filter(e => (e.service_requested || "").toLowerCase().includes(filters.service.toLowerCase()));
    if (filters.dateFrom) {
      const fromDate = new Date(`${filters.dateFrom}T00:00:00.000`);
      filtered = filtered.filter(e => e.created && new Date(e.created) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(`${filters.dateTo}T23:59:59.999`);
      filtered = filtered.filter(e => e.created && new Date(e.created) <= toDate);
    }
    if (filters.followUp && filters.followUp !== 'all') {
      filtered = filtered.filter(e => {
        const meta = getFollowUpMeta(e.follow_up_date);
        if (filters.followUp === 'none') return !meta;
        if (!meta) return false;
        if (filters.followUp === 'due') return meta.state === 'today' || meta.state === 'overdue';
        return meta.state === filters.followUp;
      });
    }
    setFilteredEnquiries(filtered);
    
    // Clear selection if filtered results don't contain them (optional, but safer)
    const filteredIds = new Set(filtered.map(f => f.id));
    setSelectedIds(prev => new Set([...prev].filter(id => filteredIds.has(id))));
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters(initialFilters);
    setSelectedSavedViewId('');
  };

  const handleToggleSourceFilter = (source) => {
    setFilters(prev => ({
      ...prev,
      source_channel: prev.source_channel.includes(source)
        ? prev.source_channel.filter(s => s !== source)
        : [...prev.source_channel, source]
    }));
  };

  const getSavedViewsStorageKey = () => {
    const tenantId = getEffectiveTenantId() || 'no-tenant';
    const userId = currentUser?.id || currentUser?.email || 'user';
    return `bpas_enquiry_saved_views_v1:${tenantId}:${userId}`;
  };

  const persistSavedViews = (next) => {
    setSavedViews(next);
    try {
      window.localStorage.setItem(getSavedViewsStorageKey(), JSON.stringify(next));
    } catch {
      setError('Saved views could not be stored in this browser.');
    }
  };

  const handleSaveCurrentView = () => {
    const requestedName = window.prompt('Name this enquiry view:');
    const name = requestedName?.trim();
    if (!name) return;
    const existing = savedViews.find(v => v.name.toLowerCase() === name.toLowerCase());
    const view = {
      id: existing?.id || (window.crypto?.randomUUID?.() || `view-${Date.now()}`),
      name,
      searchTerm,
      filters: { ...filters, status: [...filters.status], enquiry_type: [...filters.enquiry_type], source_channel: [...filters.source_channel] }
    };
    const next = existing ? savedViews.map(v => v.id === existing.id ? view : v) : [...savedViews, view];
    persistSavedViews(next);
    setSelectedSavedViewId(view.id);
    setSuccess(existing ? `Saved view "${name}" updated.` : `Saved view "${name}" created.`);
    setTimeout(() => setSuccess(''), 2500);
  };

  const handleApplySavedView = (viewId) => {
    setSelectedSavedViewId(viewId);
    if (!viewId) return;
    const view = savedViews.find(v => v.id === viewId);
    if (!view) return;
    setSearchTerm(view.searchTerm || '');
    setFilters({
      ...initialFilters,
      ...(view.filters || {}),
      status: [...(view.filters?.status || [])],
      enquiry_type: [...(view.filters?.enquiry_type || [])],
      source_channel: [...(view.filters?.source_channel || [])]
    });
  };

  const handleDeleteSavedView = () => {
    if (!selectedSavedViewId) return;
    const view = savedViews.find(v => v.id === selectedSavedViewId);
    if (!view || !window.confirm(`Delete saved view "${view.name}"?`)) return;
    persistSavedViews(savedViews.filter(v => v.id !== selectedSavedViewId));
    setSelectedSavedViewId('');
    setSuccess('Saved view deleted.');
    setTimeout(() => setSuccess(''), 2500);
  };

  const handleDeleteEnquiry = async (enquiry) => {
    if (!canDeleteEnquiries) return;
    if (!window.confirm(`Delete enquiry for ${enquiry.full_name}?`)) return;
    try {
      await deleteEnquiry(enquiry.id);
      setEnquiries(prev => prev.filter(x => x.id !== enquiry.id));
      setSuccess("Enquiry deleted");
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); }
  };

  const handleSaveEnquiry = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...enquiryFormData,
        follow_up_date: enquiryFormData.follow_up_date ? toPocketBaseDate(enquiryFormData.follow_up_date) : ''
      };
      if (enquiryFormData.id) {
        const updated = await updateEnquiry(enquiryFormData.id, payload);
        setEnquiries(enquiries.map(item => item.id === updated.id ? updated : item));
        setSuccess('Enquiry updated');
      } else {
        const tenantId = getEffectiveTenantId();
        const created = await createEnquiryForTenant(tenantId, payload);
        setEnquiries([created, ...enquiries]);
        setSuccess('Enquiry created');
      }
      setIsEnquiryModalOpen(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message); }
  };

  const handleSaveStatus = async (id, s) => {
    try {
      await updateEnquiry(id, { status: s });
      setEnquiries(enquiries.map(e => e.id === id ? { ...e, status: s } : e));
      setEditingStatus(null);
    } catch (err) { setError(err.message); }
  };

  const handleSavePriority = async (id, p) => {
    try {
      await updateEnquiry(id, { priority: p });
      setEnquiries(enquiries.map(e => e.id === id ? { ...e, priority: p } : e));
      setEditingPriority(null);
    } catch (err) { setError(err.message); }
  };

  const handleSaveFollowUp = async (id, dateValue) => {
    try {
      const storedValue = dateValue ? toPocketBaseDate(dateValue) : '';
      const updated = await updateEnquiry(id, { follow_up_date: storedValue });
      setEnquiries(prev => prev.map(e => e.id === id ? { ...e, follow_up_date: updated.follow_up_date || storedValue } : e));
      setSuccess(dateValue ? `Follow-up set for ${format(new Date(`${dateValue}T00:00:00`), 'dd MMM yyyy')}.` : 'Follow-up date cleared.');
      setTimeout(() => setSuccess(''), 2200);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddNote = async (enquiry, text) => {
    if (!text?.trim() || !enquiry) return;
    const latestEnquiry = enquiries.find(item => item.id === enquiry.id) || enquiry;
    const note = { 
      text: text.trim(), 
      created_at: new Date().toISOString(), 
      created_by: currentUser?.display_name || currentUser?.email || 'User' 
    };
    const existing = safeParseNotes(latestEnquiry.internal_note);
    const updated = [note, ...existing];
    const serializedNotes = JSON.stringify(updated);
    try {
      await updateEnquiry(enquiry.id, { internal_note: serializedNotes });
      setEnquiries(prev => prev.map(enq => enq.id === enquiry.id ? { ...enq, internal_note: serializedNotes } : enq));
      if (selectedEnquiryForNotes?.id === enquiry.id) {
        setCurrentNotes(updated);
        setSelectedEnquiryForNotes(prev => prev ? { ...prev, internal_note: serializedNotes } : prev);
      }
      setNewNoteText('');
    } catch (err) { setError(err.message); }
  };

  // --- BULK OPERATIONS ---
  const handleToggleSelectAll = () => {
    if (selectedIds.size === filteredEnquiries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEnquiries.map(e => e.id)));
    }
  };

  const handleSelectRow = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleApplyBulkActions = async () => {
    const list = Array.from(selectedIds);
    if (list.length === 0) return;

    const summary = `Update ${list.length} enquiries with:${bulkUpdateData.status ? `\n- Status: ${bulkUpdateData.status}` : ''}${bulkUpdateData.priority ? `\n- Priority: ${bulkUpdateData.priority}` : ''}${bulkUpdateData.note ? `\n- Append Note: "${bulkUpdateData.note}"` : ''}\n\nProceed?`;
    
    if (!window.confirm(summary)) return;

    setIsLoading(true);
    let successCount = 0;
    try {
      for (const id of list) {
        const enquiry = enquiries.find(e => e.id === id);
        if (!enquiry) continue;

        const payload = {};
        if (bulkUpdateData.status) payload.status = bulkUpdateData.status;
        if (bulkUpdateData.priority) payload.priority = bulkUpdateData.priority;
        
        if (bulkUpdateData.note) {
          const note = { 
            text: bulkUpdateData.note.trim(), 
            created_at: new Date().toISOString(), 
            created_by: currentUser?.display_name || currentUser?.email || 'User' 
          };
          const existing = safeParseNotes(enquiry.internal_note);
          payload.internal_note = JSON.stringify([note, ...existing]);
        }

        if (Object.keys(payload).length > 0) {
          await updateEnquiry(id, payload);
          successCount++;
        }
      }
      setSuccess(`Updated ${successCount} enquiries successfully.`);
      setSelectedIds(new Set());
      setBulkUpdateData({ status: '', priority: '', note: '' });
      fetchEnquiriesData();
    } catch (err) {
      setError("Bulk update partially failed: " + err.message);
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // --- IMPORT / EXPORT ---
  const handleImportClick = () => fileInputRef.current?.click();
  
  const buildReasonCounts = (issues) => {
    const map = {};
    (issues || []).forEach(r => {
      const reason = r["Import Result Reason"] || "Unspecified";
      map[reason] = (map[reason] || 0) + 1;
    });
    return map;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportStats({ processing: true, processed: 0, total: 0, added: 0, skipped: 0, failed: 0, issueRecords: [] });

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const tenantId = getEffectiveTenantId();

        if (!tenantId) throw new Error('No active tenant context is available for this import.');

        const knownKeys = new Set(
          (enquiries || []).map(item => makeExactDupKey({ full_name: item.full_name, email: item.email, phone: item.phone }))
        );

        setImportStats(ps => ({ ...ps, total: data.length }));

        const issues = [];
        let addedCount = 0;
        let skippedCount = 0;
        let failedCount = 0;
        let hasAttemptedCreate = false;

        const addIssue = (row, mapped, reason, details = '') => {
          issues.push({
            ...row,
            'Mapped Full Name': mapped.full_name || '',
            'Mapped Email': mapped.email || '',
            'Mapped Phone': mapped.phone || '',
            'Import Result Reason': reason,
            'Import Error Details': details
          });
        };

        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          const mapped = mapImportRow(row);
          const enquiryType = normalizeImportEnquiryType(mapped.enquiry_type);
          const key = makeExactDupKey(mapped);

          if (!mapped.full_name) {
            failedCount++;
            addIssue(row, mapped, 'Validation failed', 'Full Name is required. Recognised headings include Full Name, Name, Contact Name, Client Name, or First Name + Last Name.');
          } else if (!isValidEmail(mapped.email)) {
            failedCount++;
            addIssue(row, mapped, 'Validation failed', `Invalid email address: ${mapped.email}`);
          } else if (!enquiryType) {
            failedCount++;
            addIssue(row, mapped, 'Validation failed', `Unsupported enquiry type: ${mapped.enquiry_type}`);
          } else if (knownKeys.has(key)) {
            skippedCount++;
            addIssue(row, mapped, 'Duplicate', 'Exact duplicate detected using Name + Email + Phone.');
          } else {
            knownKeys.add(key);
            if (hasAttemptedCreate) await wait(250);
            hasAttemptedCreate = true;

            try {
              await createEnquiryForTenant(tenantId, {
                full_name: mapped.full_name,
                email: mapped.email,
                phone: mapped.phone,
                enquiry_type: enquiryType,
                service_requested: mapped.service_requested,
                message: mapped.message,
                source_channel: mapped.source_channel,
                status: 'new',
                priority: 'normal'
              });
              addedCount++;
            } catch (err) {
              failedCount++;
              addIssue(row, mapped, 'PocketBase create failed', err?.message || 'Unknown PocketBase validation error.');
            }
          }

          const processedCount = i + 1;
          setImportStats(ps => ({
            ...ps,
            processed: processedCount,
            added: addedCount,
            skipped: skippedCount,
            failed: failedCount
          }));
        }

        const finalIssues = [...issues];
        setImportStats(ps => ({ ...ps, processing: false, processed: data.length, issueRecords: finalIssues }));

        setImportSummary({
          total: data.length,
          processed: data.length,
          added: addedCount,
          skipped: skippedCount,
          failed: failedCount,
          reasonCounts: buildReasonCounts(finalIssues),
          preview: finalIssues.slice(0, 5),
          issueRecords: finalIssues
        });
        setImportSummaryOpen(true);

        setSuccess('Import process complete.');
        fetchEnquiriesData();
      } catch (err) {
        setError('Import failed: ' + err.message);
        setImportStats(ps => ({ ...ps, processing: false }));
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  const handleDownloadErrorLog = () => {
    const issues = importStats.issueRecords.length > 0 ? importStats.issueRecords : (importSummary?.issueRecords || []);
    if (issues.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(issues);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Import Errors");
    XLSX.writeFile(wb, `Import_Errors_${format(new Date(), 'yyyy-MM-dd_HHmm')}.xlsx`);
  };

  const handleExport = () => {
    const data = filteredEnquiries.map(e => ({ 'Full Name': e.full_name, 'Email': e.email, 'Phone': e.phone, 'Type': e.enquiry_type, 'Service': e.service_requested, 'Status': e.status, 'Created': e.created }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    XLSX.writeFile(wb, `Enquiries_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const downloadImportTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{ 'Full Name': 'John Doe', 'Email': 'john@example.com', 'Phone': '0400000000', 'Type': 'general', 'Service': 'Web Design', 'Message': 'Hello', 'Source': 'Website' }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Enquiry_Template.xlsx");
  };

  const downloadDuplicateReportCsv = (groups) => {
    const lines = ["Group Key,Record ID,Name,Email", ...groups.flatMap(g => g.records.map(r => `${g.key},${r.id},${r.full_name},${r.email}`))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "duplicates.csv"; a.click();
  };

  const getStatusBadgeColor = (s) => {
    const colors = { 'new': 'bg-blue-100 text-blue-800', 'in_progress': 'bg-yellow-100 text-yellow-800', 'contacted': 'bg-purple-100 text-purple-800', 'converted': 'bg-green-100 text-green-800', 'lost': 'bg-red-100 text-red-800' };
    return colors[s] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading && enquiries.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-12 bg-gray-200 rounded w-full"></div>
          <div className="h-64 bg-gray-200 rounded-lg w-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Sticky Header Actions */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 -mx-6 px-6 py-4 shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900">Enquiries</h1>
            {bulkMode && (
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold flex items-center">
                <SafeIcon icon={FiZap} className="mr-1.5" />
                Bulk Mode: {selectedIds.size} Selected
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            {!bulkMode ? (
              <>
                <button onClick={() => { setEnquiryFormData({ id: null, full_name: '', email: '', phone: '', enquiry_type: 'general', service_requested: '', message: '', source_channel: 'Client Hub (Manual)', status: 'new', priority: 'normal', follow_up_date: '' }); setIsEnquiryModalOpen(true); }} className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors shadow-sm" data-tooltip="Create a new enquiry manually in this tenant"><SafeIcon icon={FiPlus} className="w-4 h-4 mr-1.5" /> New Enquiry</button>
                <button onClick={handleImportClick} className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" data-tooltip="Import enquiries from a CSV or Excel file using the universal importer"><SafeIcon icon={FiUpload} className="w-4 h-4 mr-1.5" /> Import</button>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".xlsx,.csv" />
                <button onClick={handleExport} className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" data-tooltip="Export the enquiries currently included by your active filters"><SafeIcon icon={FiDownload} className="w-4 h-4 mr-1.5" /> Export</button>
                <button onClick={downloadImportTemplate} className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors" data-tooltip="Download a ready-to-use spreadsheet template for enquiry imports"><SafeIcon icon={FiFileText} className="w-4 h-4 mr-1.5" /> Template</button>
                <div className="w-px h-8 bg-gray-200 mx-1 hidden md:block" />
              </>
            ) : (
              <>
                {selectedIds.size > 0 && (
                  <button onClick={() => setSelectedIds(new Set())} className="flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md font-medium transition-colors"><SafeIcon icon={FiX} className="w-4 h-4 mr-1.5" /> Clear Selection</button>
                )}
              </>
            )}
            <button 
              onClick={() => {
                setBulkMode(!bulkMode);
                if (bulkMode) setSelectedIds(new Set());
              }} 
              className={`flex items-center px-3 py-2 text-sm border rounded-md font-medium transition-colors ${bulkMode ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              data-tooltip={bulkMode ? 'Return to normal single-enquiry actions' : 'Select multiple enquiries and apply a note, status or priority update together'}
            >
              <SafeIcon icon={FiZap} className="w-4 h-4 mr-1.5" />
              {bulkMode ? 'Exit Bulk Mode' : 'Bulk Mode'}
            </button>
          </div>
        </div>

        {/* Bulk Control Bar */}
        <AnimatePresence>
          {bulkMode && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }} 
              animate={{ height: 'auto', opacity: 1 }} 
              exit={{ height: 0, opacity: 0 }}
              className="mt-4 pt-4 border-t border-gray-100"
            >
              <div className="flex flex-wrap items-end gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Apply Note to Selected</label>
                  <textarea 
                    value={bulkUpdateData.note}
                    onChange={(e) => setBulkUpdateData({...bulkUpdateData, note: e.target.value})}
                    placeholder="Add a common note to all selected..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                    rows={1}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Change Status</label>
                  <select 
                    value={bulkUpdateData.status}
                    onChange={(e) => setBulkUpdateData({...bulkUpdateData, status: e.target.value})}
                    className="px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none pr-8"
                  >
                    <option value="">No Change</option>
                    {ENQUIRY_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Change Priority</label>
                  <select 
                    value={bulkUpdateData.priority}
                    onChange={(e) => setBulkUpdateData({...bulkUpdateData, priority: e.target.value})}
                    className="px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none pr-8"
                  >
                    <option value="">No Change</option>
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <button 
                  onClick={handleApplyBulkActions}
                  disabled={selectedIds.size === 0 || (!bulkUpdateData.status && !bulkUpdateData.priority && !bulkUpdateData.note)}
                  className="px-6 py-2 bg-blue-600 text-white rounded font-bold text-sm hover:bg-blue-700 disabled:opacity-50 shadow-sm transition-all h-[38px]"
                >
                  Apply to {selectedIds.size}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Import Progress Alert (Inline) */}
        <AnimatePresence>
          {importStats.processing && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center font-bold text-blue-800">
                    <SafeIcon icon={FiLoader} className="w-5 h-5 mr-2 animate-spin" />
                    Processing Import: {importStats.processed} of {importStats.total}...
                  </div>
                  <div className="text-xs font-bold uppercase tracking-widest text-blue-600">
                    {Math.round((importStats.processed / (importStats.total || 1)) * 100)}%
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3 text-xs">
                  <span>Total: <strong>{importStats.total}</strong></span>
                  <span>Processed: <strong>{importStats.processed}</strong></span>
                  <span>Added: <strong>{importStats.added}</strong></span>
                  <span>Skipped: <strong>{importStats.skipped}</strong></span>
                  <span>Failed: <strong>{importStats.failed}</strong></span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <motion.div 
                    className="bg-blue-600 h-full" 
                    initial={{ width: 0 }} 
                    animate={{ width: `${(importStats.processed / (importStats.total || 1)) * 100}%` }} 
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Duplicate Audit Bar */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="text-sm">
            <span className="font-medium text-gray-700">Duplicate audit:</span>{" "}
            {duplicateGroups.length > 0 ? (
              <span className="text-red-700 font-semibold">{duplicateGroups.length} exact duplicate groups found</span>
            ) : (
              <span className="text-green-700 font-medium">No exact duplicates found</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowDuplicatePanel(!showDuplicatePanel)} disabled={duplicateGroups.length === 0} className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center font-medium">
              {showDuplicatePanel ? <SafeIcon icon={FiChevronUp} className="mr-1.5" /> : <SafeIcon icon={FiChevronDown} className="mr-1.5" />}
              {showDuplicatePanel ? "Hide Details" : "View Details"}
            </button>
            <button onClick={() => downloadDuplicateReportCsv(duplicateGroups)} disabled={duplicateGroups.length === 0} className="px-3 py-1.5 text-xs bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 disabled:opacity-50 font-medium">Download Report</button>
          </div>
        </div>

        <AnimatePresence>
          {showDuplicatePanel && duplicateGroups.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-6">
              <div className="bg-red-50 border border-red-100 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {duplicateGroups.slice(0, 9).map((g, idx) => (
                  <div key={idx} className="bg-white border border-red-200 rounded p-2 text-xs shadow-sm">
                    <div className="font-bold text-gray-900 border-b pb-1 mb-1 flex justify-between">
                      <span>Group {idx + 1}</span>
                      <span className="bg-red-100 text-red-800 px-1.5 rounded">{g.count} records</span>
                    </div>
                    <div className="text-gray-600 truncate">{g.key}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filters Grid */}
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 mb-6 space-y-6">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
            <h3 className="text-sm font-bold text-gray-900 flex items-center">Search & Filters <SafeIcon icon={FiInfo} className="w-3.5 h-3.5 ml-2 text-gray-400" title="All counters, exports and saved views use the currently filtered enquiry set" /></h3>
            <div className="flex flex-wrap items-center gap-2">
              <select value={selectedSavedViewId} onChange={(e) => handleApplySavedView(e.target.value)} className="px-3 py-1.5 text-xs border border-gray-300 rounded-md bg-white min-w-[150px]" title="Apply a saved filter/search combination for this user and tenant">
                <option value="">Saved views...</option>
                {savedViews.map(view => <option key={view.id} value={view.id}>{view.name}</option>)}
              </select>
              <button onClick={handleSaveCurrentView} className="flex items-center px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100" title="Save the current search, date, source, status, service and follow-up filters"><SafeIcon icon={FiBookmark} className="w-3.5 h-3.5 mr-1.5" /> Save View</button>
              {selectedSavedViewId && <button onClick={handleDeleteSavedView} className="flex items-center px-3 py-1.5 text-xs font-bold text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50" title="Delete the selected saved view"><SafeIcon icon={FiTrash2} className="w-3.5 h-3.5 mr-1.5" /> Delete View</button>}
              <button onClick={handleClearFilters} className="flex items-center text-xs font-bold text-red-600 hover:text-red-700 transition-colors px-2 py-1.5" title="Clear search and all active filters">
                <SafeIcon icon={FiRefreshCcw} className="w-3 h-3 mr-1" /> Clear All
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Search Contact</label>
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                <input type="text" placeholder="Name, email, phone..." className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Service Requested</label>
              <input type="text" placeholder="Service type..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" value={filters.service} onChange={(e) => setFilters({ ...filters, service: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">From Date</label>
              <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" title="Inclusive: enquiries created at any time on this date are included">To Date</label>
              <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" title="Filter enquiries by their follow-up date">Follow-up</label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white" value={filters.followUp} onChange={(e) => setFilters({ ...filters, followUp: e.target.value })}>
                <option value="all">All follow-ups</option>
                <option value="due">Due / overdue</option>
                <option value="overdue">Overdue only</option>
                <option value="today">Due today</option>
                <option value="upcoming">Upcoming</option>
                <option value="none">No follow-up date</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-4 pt-4 border-t border-gray-100">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2" title="Status counters recalculate from the current filtered dataset">Filter by Status</label>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1.5 rounded-full border text-xs font-bold bg-gray-50 text-gray-700 border-gray-300">
                  Total ({statusCounts.total || 0})
                </span>
                {(ENQUIRY_STATUSES || []).map(s => (
                  <button key={s.value} onClick={() => setFilters({ ...filters, status: filters.status.includes(s.value) ? filters.status.filter(x => x !== s.value) : [...filters.status, s.value] })} className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${filters.status.includes(s.value) ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {s.label} ({statusCounts[s.value] || 0})
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2" title="Source labels are normalised for filtering without rewriting saved PocketBase values">Filter by Source Channel</label>
              <div className="flex flex-wrap gap-2">
                {(SOURCE_CHANNELS || []).map(source => (
                  <button key={source} onClick={() => handleToggleSourceFilter(source)} className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${filters.source_channel.includes(source) ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {source}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Global Feedback */}
        {(error || success) && (
          <div className={`p-4 rounded-md mb-6 border shadow-sm flex items-center ${error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
            <SafeIcon icon={error ? FiAlertTriangle : FiCheck} className="mr-3" />
            <div className="flex-1">{error || success}</div>
            <button onClick={() => { setError(''); setSuccess(''); }}><SafeIcon icon={FiX} /></button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 text-xs font-bold text-gray-500 uppercase tracking-wider">
                <tr>
                  {bulkMode && (
                    <th className="px-4 py-4 w-10">
                      <input 
                        type="checkbox" 
                        checked={selectedIds.size === filteredEnquiries.length && filteredEnquiries.length > 0}
                        onChange={handleToggleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-6 py-4 text-left">Contact</th>
                  <th className="px-6 py-4 text-left">Service</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-left">Priority</th>
                  <th className="px-6 py-4 text-left">Date</th>
                  <th className="px-6 py-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEnquiries.map(enquiry => {
                  const isExpanded = expandedEnquiryId === enquiry.id;
                  const isSelected = selectedIds.has(enquiry.id);
                  
                  return (
                    <React.Fragment key={enquiry.id}>
                      <tr 
                        onClick={(e) => {
                          if (e.target.closest('button, select, input, a')) return;
                          if (bulkMode) handleSelectRow(enquiry.id);
                          else setExpandedEnquiryId(isExpanded ? null : enquiry.id);
                        }}
                        className={`transition-colors cursor-pointer ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        {bulkMode && (
                          <td className="px-4 py-4 text-center">
                            <input 
                              type="checkbox" 
                              checked={isSelected}
                              onChange={() => handleSelectRow(enquiry.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">{enquiry.full_name}</div>
                          <div className="flex flex-col space-y-0.5 mt-1">
                            <a href={`mailto:${enquiry.email}`} onClick={e => e.stopPropagation()} className="text-xs text-blue-600 hover:underline flex items-center font-medium" data-tooltip="Open a new email to this contact">
                              <SafeIcon icon={FiMail} className="w-3 h-3 mr-1 opacity-70" />
                              {enquiry.email}
                            </a>
                            {enquiry.phone && (
                              <a href={`tel:${normalizeDigits(enquiry.phone)}`} onClick={e => e.stopPropagation()} className="text-xs text-gray-600 hover:text-blue-700 flex items-center font-medium" data-tooltip="Call this contact using your device phone handler">
                                <SafeIcon icon={FiPhone} className="w-3 h-3 mr-1 opacity-70" />
                                {enquiry.phone}
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-gray-900 font-medium">{enquiry.service_requested || 'General'}</div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-tight font-bold mt-1">Source: {normalizeSource(enquiry.source_channel)}</div>
                        </td>
                        <td className="px-6 py-4">
                          {editingStatus === enquiry.id ? (
                            <div className="flex items-center gap-1">
                              <select 
                                className="text-xs border border-gray-300 rounded px-2 py-1 outline-none" 
                                defaultValue={enquiry.status} 
                                onChange={(e) => handleSaveStatus(enquiry.id, e.target.value)}
                                onClick={e => e.stopPropagation()}
                              >
                                {ENQUIRY_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                              </select>
                              <button onClick={e => { e.stopPropagation(); setEditingStatus(null); }} className="text-gray-400 hover:text-red-500"><SafeIcon icon={FiX} className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <button onClick={e => { e.stopPropagation(); setEditingStatus(enquiry.id); }} className={`px-2.5 py-1 rounded-full text-xs font-bold shadow-sm transition-opacity hover:opacity-80 ${getStatusBadgeColor(enquiry.status)}`} data-tooltip="Change this enquiry status inline">
                              {enquiry.status}
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {editingPriority === enquiry.id ? (
                            <div className="flex items-center gap-1">
                              <select 
                                className="text-xs border border-gray-300 rounded px-2 py-1 outline-none" 
                                defaultValue={enquiry.priority} 
                                onChange={(e) => handleSavePriority(enquiry.id, e.target.value)}
                                onClick={e => e.stopPropagation()}
                              >
                                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
                              </select>
                              <button onClick={e => { e.stopPropagation(); setEditingPriority(null); }} className="text-gray-400 hover:text-red-500"><SafeIcon icon={FiX} className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <button onClick={e => { e.stopPropagation(); setEditingPriority(enquiry.id); }} className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-800 transition-colors hover:bg-gray-200" data-tooltip="Change this enquiry priority inline">
                              {enquiry.priority || 'normal'}
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-500">
                          <div>{enquiry.created ? format(new Date(enquiry.created), 'dd MMM yyyy') : 'N/A'}</div>
                          {getFollowUpMeta(enquiry.follow_up_date) && (
                            <div className={`inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold ${getFollowUpMeta(enquiry.follow_up_date).classes}`} title="Follow-up date">
                              <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" /> {getFollowUpMeta(enquiry.follow_up_date).label}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 whitespace-nowrap" aria-label="Enquiry quick actions">
                            <button type="button" onClick={e => { e.stopPropagation(); setSelectedEnquiry(enquiry); setReplyModalOpen(true); }} className="theme-inline-action-button" data-tooltip="Reply to this enquiry" aria-label="Reply to enquiry"><SafeIcon icon={FiCornerUpLeft} className="w-4 h-4" /></button>
                            <button type="button" onClick={e => { e.stopPropagation(); setEnquiryFormData({ ...enquiry, follow_up_date: getDateInputValue(enquiry.follow_up_date) }); setIsEnquiryModalOpen(true); }} className="theme-inline-action-button" data-tooltip="Edit enquiry details and follow-up date" aria-label="Edit enquiry"><SafeIcon icon={FiEdit3} className="w-4 h-4" /></button>
                            <button type="button" onClick={e => { e.stopPropagation(); setSelectedEnquiryForNotes(enquiry); setCurrentNotes(safeParseNotes(enquiry.internal_note)); setNotesModalOpen(true); }} className="theme-inline-action-button" data-tooltip="Open all internal notes and add a new note" aria-label="Open enquiry notes"><SafeIcon icon={FiMessageSquare} className="w-4 h-4" /></button>
                            <button type="button" onClick={e => { e.stopPropagation(); setExpandedEnquiryId(isExpanded ? null : enquiry.id); }} className={`theme-inline-action-button ${isExpanded ? 'theme-inline-action-button-active' : ''}`} data-tooltip={isExpanded ? 'Collapse enquiry details' : 'Expand enquiry details and quick management'} aria-label={isExpanded ? 'Collapse enquiry details' : 'Expand enquiry details'}><SafeIcon icon={FiChevronDown} className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} /></button>
                          </div>
                        </td>
                      </tr>

                      {/* Row Expand Drawer */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.tr 
                            initial={{ opacity: 0, height: 0 }} 
                            animate={{ opacity: 1, height: 'auto' }} 
                            exit={{ opacity: 0, height: 0 }}
                            className="bg-gray-50/80 border-l-4 border-blue-500"
                          >
                            <td colSpan={bulkMode ? 7 : 6} className="px-6 py-6">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Left Side: Message & Notes */}
                                <div className="space-y-6">
                                  <div>
                                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center">
                                      <SafeIcon icon={FiMessageSquare} className="mr-1.5" /> Client Message
                                    </h4>
                                    <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                      {enquiry.message || <span className="text-gray-400 italic">No message provided</span>}
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Recent Activity Log</h4>
                                    <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                      {safeParseNotes(enquiry.internal_note).slice(0, 3).map((n, i) => (
                                        <div key={i} className="bg-white p-3 rounded border border-gray-100 shadow-sm">
                                          <p className="text-xs text-gray-700 whitespace-pre-wrap">{n.text}</p>
                                          <p className="text-[9px] text-gray-400 mt-1 uppercase font-bold">
                                            {n.created_by || 'Legacy note'}{formatNoteTimestamp(n.created_at) ? ` • ${formatNoteTimestamp(n.created_at)}` : ''}
                                          </p>
                                        </div>
                                      ))}
                                      {safeParseNotes(enquiry.internal_note).length === 0 && (
                                        <p className="text-xs text-gray-400 italic">No notes yet.</p>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Right Side: Actions & Quick Note */}
                                <div className="space-y-6">
                                  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm space-y-4">
                                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Quick Add Note</h4>
                                    <div className="flex gap-2">
                                      <input 
                                        ref={quickNoteInputRef}
                                        type="text" 
                                        placeholder="Type a quick internal note..." 
                                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && e.target.value.trim()) {
                                            handleAddNote(enquiry, e.target.value);
                                            e.target.value = '';
                                          }
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const value = quickNoteInputRef.current?.value || '';
                                          if (!value.trim()) return;
                                          handleAddNote(enquiry, value);
                                          quickNoteInputRef.current.value = '';
                                        }}
                                        className="px-3 py-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                                        title="Add note"
                                      >
                                        <SafeIcon icon={FiPlus} />
                                      </button>
                                    </div>
                                    
                                    <div className="pt-4 border-t border-gray-100">
                                      <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Quick Management</h4>
                                      <div className="mb-4">
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5" title="This date is stored on the existing PocketBase follow_up_date field">Follow-up Date</label>
                                        <div className="flex items-center gap-2">
                                          <input type="date" value={getDateInputValue(enquiry.follow_up_date)} onChange={(e) => handleSaveFollowUp(enquiry.id, e.target.value)} className="px-3 py-2 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" />
                                          {enquiry.follow_up_date && <button type="button" onClick={() => handleSaveFollowUp(enquiry.id, '')} className="px-3 py-2 text-xs font-bold text-gray-500 hover:text-red-600" title="Clear follow-up date">Clear</button>}
                                        </div>
                                        {getFollowUpMeta(enquiry.follow_up_date) && <p className="text-[10px] text-gray-500 mt-1.5">{getFollowUpMeta(enquiry.follow_up_date).label}</p>}
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        <button 
                                          onClick={() => { setSelectedEnquiry(enquiry); setReplyModalOpen(true); }}
                                          className="flex items-center px-4 py-2 text-xs font-bold bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
                                        >
                                          <SafeIcon icon={FiCornerUpLeft} className="mr-2" /> Reply
                                        </button>
                                        <button 
                                          onClick={() => { setSelectedEnquiryForNotes(enquiry); setCurrentNotes(safeParseNotes(enquiry.internal_note)); setNotesModalOpen(true); }}
                                          className="flex items-center px-4 py-2 text-xs font-bold bg-gray-50 text-gray-700 rounded hover:bg-gray-100 transition-colors"
                                        >
                                          <SafeIcon icon={FiMoreHorizontal} className="mr-2" /> All Notes
                                        </button>
                                        {canDeleteEnquiries && (
                                          <button 
                                            onClick={() => handleDeleteEnquiry(enquiry)}
                                            className="flex items-center px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded transition-colors"
                                          >
                                            <SafeIcon icon={FiTrash2} className="mr-2" /> Delete
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </motion.tr>
                        )}
                      </AnimatePresence>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* --- MODALS --- */}

      {/* Import Summary Modal */}
      <AnimatePresence>
        {importSummaryOpen && importSummary && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden">
              <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Import Results</h2>
                </div>
                <button onClick={() => setImportSummaryOpen(false)} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} className="w-6 h-6" /></button>
              </div>

              <div className="p-8 space-y-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  <div className="text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Total</p>
                    <p className="text-2xl font-bold text-gray-900">{importSummary.total}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Processed</p>
                    <p className="text-2xl font-bold text-blue-600">{importSummary.processed}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Added</p>
                    <p className="text-2xl font-bold text-green-600">{importSummary.added}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Skipped</p>
                    <p className="text-2xl font-bold text-orange-600">{importSummary.skipped}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Failed</p>
                    <p className="text-2xl font-bold text-red-600">{importSummary.failed}</p>
                  </div>
                </div>

                {/* Reason Breakdown */}
                {Object.keys(importSummary.reasonCounts).length > 0 && (
                  <div>
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Not Imported Breakdown</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 border border-gray-100">
                      {Object.entries(importSummary.reasonCounts).map(([reason, count]) => (
                        <div key={reason} className="flex justify-between items-center text-sm">
                          <span className="text-gray-700 flex items-center">
                            <SafeIcon icon={FiAlertTriangle} className={`mr-2 w-3.5 h-3.5 ${reason === 'Duplicate' ? 'text-orange-500' : 'text-red-500'}`} />
                            {reason === 'Duplicate' ? 'Skipped: Exact duplicate (Name + Email + Phone)' : reason}
                          </span>
                          <span className="font-bold text-gray-900">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview of Issues */}
                {importSummary.preview.length > 0 && (
                  <div>
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Issue Preview (First 5)</h3>
                    <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-400 uppercase">Record</th>
                            <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-400 uppercase">Result</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {importSummary.preview.map((row, idx) => (
                            <tr key={idx} className="text-xs">
                              <td className="px-4 py-2">
                                <p className="font-bold text-gray-900">{row['Mapped Full Name'] || row['Full Name'] || 'Unknown'}</p>
                                <p className="text-gray-500">{row['Mapped Email'] || row['Mapped Phone'] || row['Email'] || row['Phone'] || 'No contact info'}</p>
                              </td>
                              <td className="px-4 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${row['Import Result Reason'] === 'Duplicate' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>
                                  {row['Import Result Reason']}
                                </span>
                                {row['Import Error Details'] && (
                                  <p className="mt-1 text-[10px] text-gray-500 whitespace-pre-wrap break-words">{row['Import Error Details']}</p>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-6 bg-gray-50 flex gap-3">
                <button onClick={() => setImportSummaryOpen(false)} className="flex-1 px-4 py-2 text-sm font-bold text-gray-600 hover:text-gray-800 transition-colors">Close</button>
                {(importSummary.skipped > 0 || importSummary.failed > 0) && (
                  <button 
                    onClick={handleDownloadErrorLog}
                    className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-bold shadow-md transition-all"
                  >
                    <SafeIcon icon={FiDownload} className="mr-2" />
                    Download Error Log
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Internal Notes Modal */}
      <AnimatePresence>
        {notesModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-lg p-6 max-w-lg w-full shadow-xl">
              <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h2 className="text-lg font-bold text-gray-900">Enquiry Activity & Notes</h2>
                <button onClick={() => setNotesModalOpen(false)}><SafeIcon icon={FiX} className="w-5 h-5" /></button>
              </div>
              <div className="max-h-80 overflow-y-auto mb-4 space-y-4 pr-2">
                {selectedEnquiryForNotes?.message && (
                  <div className="bg-blue-50 p-3 rounded text-xs border border-blue-100">
                    <p className="font-bold text-blue-900 mb-1 uppercase tracking-tight">Client Message</p>
                    <p className="text-blue-800 whitespace-pre-wrap">{selectedEnquiryForNotes.message}</p>
                  </div>
                )}
                <div className="space-y-3">
                  {currentNotes.length === 0 ? <p className="text-gray-400 text-sm italic">No internal notes added yet.</p> : currentNotes.map((n, i) => (
                    <div key={i} className="bg-gray-50 p-3 rounded text-sm border border-gray-100 shadow-sm transition-all hover:bg-white">
                      <p className="text-gray-800 whitespace-pre-wrap">{n.text}</p>
                      <p className="text-[10px] text-gray-400 mt-1 uppercase font-bold">
                        {n.created_by || 'Legacy note'}{formatNoteTimestamp(n.created_at) ? ` • ${formatNoteTimestamp(n.created_at)}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <textarea className="w-full border border-gray-300 p-3 rounded-lg text-sm mb-4 outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-inner" placeholder="Add an internal note..." rows={3} value={newNoteText} onChange={e => setNewNoteText(e.target.value)} />
              <div className="flex justify-end gap-2">
                <button onClick={() => setNotesModalOpen(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium">Close</button>
                <button onClick={(e) => handleAddNote(selectedEnquiryForNotes, newNoteText)} disabled={!newNoteText.trim()} className="bg-blue-600 text-white px-5 py-2 rounded-md text-sm font-bold disabled:opacity-50 shadow-sm hover:bg-blue-700">Add Note</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Enquiry Form Modal */}
      <AnimatePresence>
        {isEnquiryModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-lg p-8 max-w-lg w-full shadow-2xl">
              <h2 className="text-xl font-bold mb-6 text-gray-900 border-b pb-4">{enquiryFormData.id ? 'Edit' : 'New'} Enquiry</h2>
              <form onSubmit={handleSaveEnquiry} className="space-y-4">
                <input type="text" placeholder="Full Name *" required className="w-full border p-2.5 rounded-md outline-none focus:ring-2 focus:ring-blue-500" value={enquiryFormData.full_name} onChange={e => setEnquiryFormData({ ...enquiryFormData, full_name: e.target.value })} />
                <input type="email" placeholder="Email Address *" required className="w-full border p-2.5 rounded-md outline-none focus:ring-2 focus:ring-blue-500" value={enquiryFormData.email} onChange={e => setEnquiryFormData({ ...enquiryFormData, email: e.target.value })} />
                <input type="tel" placeholder="Phone" className="w-full border p-2.5 rounded-md outline-none" value={enquiryFormData.phone} onChange={e => setEnquiryFormData({ ...enquiryFormData, phone: e.target.value })} />
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5" title="Set the next date this enquiry should be followed up">Follow-up Date</label>
                  <input type="date" className="w-full border p-2.5 rounded-md outline-none focus:ring-2 focus:ring-blue-500" value={getDateInputValue(enquiryFormData.follow_up_date)} onChange={e => setEnquiryFormData({ ...enquiryFormData, follow_up_date: e.target.value })} />
                </div>
                <textarea placeholder="Message" className="w-full border p-2.5 rounded-md outline-none" rows={3} value={enquiryFormData.message} onChange={e => setEnquiryFormData({ ...enquiryFormData, message: e.target.value })} />
                <div className="flex justify-end gap-2 pt-4">
                  <button type="button" onClick={() => setIsEnquiryModalOpen(false)} className="px-5 py-2.5 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-5 py-2.5 bg-blue-600 text-white rounded-md font-bold hover:bg-blue-700">Save</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <EnquiryReplyModal isOpen={replyModalOpen} onClose={() => setReplyModalOpen(false)} enquiry={selectedEnquiry} />
    </div>
  );
};

export default Enquiries;